/**
 * What the backend says each screen shows (`GET /api/v2/ui`, and the head of every trajectory): the domain spec's
 * `ui:` sections, checked by the backend at load (backend/backend/config/domain_spec.py `ui_document`). Read here
 * with the same strictness as every other wire: an unreadable document is a visible problem, and the pack's own
 * screens stay in use.
 */
import { z } from 'zod';
import { report } from '../core/diagnostics';

export const UI_VERSION = 'ui/1';

const look = {
  label: z.string().optional(),
  icon: z.string().optional(),
  tone: z.enum(['primary', 'neutral', 'quiet']).optional(),
  place: z.enum(['start', 'end']).optional(),
};

const barEntry = z.union([
  z.object({ button: z.string(), data: z.record(z.string(), z.unknown()).optional(), ...look }).strict(),
  z.object({ menu: z.string() }).strict(),
  z.object({ action: z.string() }).strict(),
]);

/** What is drawn: a widget with its look and props, or a builder of the pack. */
const viewUi = z.object({
  widget: z.string().optional(),
  variant: z.string().optional(),
  build: z.string().optional(),
  props: z.record(z.string(), z.unknown()).optional(),
}).strict();

const screenUi = viewUi.extend({
  /** A session field (dotted path) whose value picks the view among `cases`. */
  by: z.string().optional(),
  cases: z.record(z.string(), viewUi).optional(),
  hint: z.string().optional(),
  step: z.string().optional(),
  bar: z.array(barEntry).optional(),
  layout: z.array(z.unknown()).optional(),
}).strict();

const uiDocument = z.object({
  version: z.literal(UI_VERSION),
  domain: z.string(),
  flow: z.array(z.string()),
  steps: z.array(z.object({ id: z.string(), label: z.string() })),
  buttons: z.record(z.string(), z.object({ action: z.string(), ...look }).strict()),
  screens: z.record(z.string(), screenUi),
  /** What the screens write, by language, flat keys (the `ui:` section of the domain's strings). Older documents: none. */
  strings: z.record(z.string(), z.record(z.string(), z.string())).default({}),
});

export type UiDocument = z.infer<typeof uiDocument>;
export type ScreenUi = z.infer<typeof screenUi>;
export type ViewUi = z.infer<typeof viewUi>;
export type BarEntry = z.infer<typeof barEntry>;

export type ParsedUi = { ok: true; document: UiDocument } | { ok: false; reason: string };

/** The document, or why it cannot be used (the reason names the first field that is wrong). */
export function parseUiDocument(raw: unknown): ParsedUi {
  const parsed = uiDocument.safeParse(raw);
  if (parsed.success) return { ok: true, document: parsed.data };
  const issue = parsed.error.issues[0];
  return { ok: false, reason: `${issue?.path.join('.') || 'document'}: ${issue?.message ?? 'unreadable'}` };
}

/** The document, or null (none sent, or unreadable: said once, and the pack's own screens stay). */
export function uiDocumentOf(raw: unknown): UiDocument | null {
  if (raw === null || raw === undefined) return null;
  const parsed = parseUiDocument(raw);
  if (!parsed.ok) report('warn', 'layout', `the screens' spec is unreadable (${parsed.reason}): the pack's own screens are shown`);
  return parsed.ok ? parsed.document : null;
}
